import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, Validators } from '@angular/forms';
import { PartnerOnboardingService } from './partner-onboarding.service';
import { ConstantsService } from '../constants.service';
import { ToastMessage } from '../toast-message/toast-message.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import './partner-onboarding.component.css';

@Component({
  selector: 'app-partner-onboarding',
  template: require('./partner-onboarding.component.html'),
  providers: [PartnerOnboardingService, ToastMessage]
})
export class PartnerOnboardingComponent implements OnInit, OnDestroy {

  public overHeadPanelTitle = 'Partner Manager Onboarding';
  public partnersData: any[] = [];
  public filteredPartnersData: any[] = [];
  public recordsPerPage = 10;
  public offset = 0;
  public loading = false;
  public canGoNext = true;
  public canGoPrevious = false;
  private subscriptions: Subscription[] = [];

  // Filter fields
  public mobileFilter: string = '';
  public statusFilter: string = '';
  public statusOptions = [
    { value: '', label: 'All' },
    { value: 'approved', label: 'Approved' },
    { value: 'processing', label: 'Awaiting Approval' },
    { value: 'revoked', label: 'Revoked' },
    { value: 'rejected', label: 'Rejected' }
  ];

  // Form controls for validation
  mobileFormControl = new FormControl('', [
    Validators.pattern('^[0-9]{10}$')
  ]);

  // Table columns
  public columnsToDisplay = ['agentName', 'phoneNumber', 'aadharNumber', 'status', 'action'];

  constructor(
    private router: Router,
    private partnerService: PartnerOnboardingService,
    private constantsService: ConstantsService,
    private toastMessage: ToastMessage
  ) { }

  ngOnInit() {
    const pagination = this.constantsService.pagination;
    this.recordsPerPage = pagination.defaultPageSize;
    this.loadPartners();

    const mobileSub = this.mobileFormControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((val: string) => {
        const cleanedValue = (val || '').trim();

        // Check if it's empty - load all data
        if (cleanedValue === '') {
          this.mobileFilter = '';
          this.offset = 0;
          this.canGoPrevious = false;
          this.loadPartnersWithCurrentFilters();
          return;
        }

        // Extract only digits
        const digits = cleanedValue.replace(/\D/g, '');

        // Only call API if we have exactly 10 digits and form is valid
        if (digits.length === 10 && this.mobileFormControl.valid && /^[0-9]{10}$/.test(digits)) {
          this.mobileFilter = digits;
          this.offset = 0;
          this.canGoPrevious = false;
          this.loadPartnersWithCurrentFilters();
        } else {
          // Invalid input - clear filter but don't call API
          this.mobileFilter = '';
        }
      });
    this.subscriptions.push(mobileSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => {
      try { s.unsubscribe(); } catch (_) { }
    });
  }

  goBack() {
    this.router.navigate(['/modelSelection']);
  }

  loadPartners() {
    this.loading = true;
    this.partnerService.getPartnerDetails(this.offset, this.recordsPerPage).subscribe(
      response => {
        this.partnersData = response.data || [];
        const received = this.partnersData.length;
        const totalCandidates = [
          response && response.total,
          response && response.count,
          response && response.info && response.info.total,
          response && response.pagination && response.pagination.total
        ];
        const firstValid = totalCandidates.find(v => typeof v === 'number' && v >= 0) as number | undefined;

        if (received === 0) {
          this.canGoNext = false;
          this.canGoPrevious = this.offset > 0;
        } else {
          this.canGoNext = received >= this.recordsPerPage;
          this.canGoPrevious = this.offset > 0;
        }

        this.filteredPartnersData = [...this.partnersData];
        this.loading = false;
      },
      error => {
        this.toastMessage.openSnackBarError('Error loading partner data');
        this.loading = false;
      }
    );
  }


  loadPartnersWithCurrentFilters() {
    // Strict validation for mobile: exactly 10 digits, all numeric 0-9
    const validMobile = this.mobileFilter &&
      this.mobileFilter.length === 10 &&
      /^[0-9]{10}$/.test(this.mobileFilter) ? this.mobileFilter : '';
    const validStatus = this.statusFilter && this.statusFilter.trim() !== '' ? this.statusFilter.trim() : '';

    if (!validMobile && !validStatus) {
      this.loadPartners();
    } else {
      this.loadPartnersWithFilters();
    }
  }

  loadPartnersWithFilters() {
    this.loading = true;
    const digitsOnly = (this.mobileFilter || '').replace(/\D/g, '');
    // Strict validation: only accept exactly 10 digits that are valid numbers 0-9
    const validMobile = this.mobileFormControl.valid &&
      digitsOnly.length === 10 &&
      /^[0-9]{10}$/.test(digitsOnly) ? digitsOnly : '';
    const selectedStatus = (this.statusFilter || '').trim();
    this.partnerService.getPartnerDetails(this.offset, this.recordsPerPage, validMobile, selectedStatus).subscribe(
      response => {
        this.partnersData = response.data || [];
        const received = this.partnersData.length;
        const totalCandidates = [
          response && response.total,
          response && response.count,
          response && response.info && response.info.total,
          response && response.pagination && response.pagination.total
        ];
        const firstValid = totalCandidates.find(v => typeof v === 'number' && v >= 0) as number | undefined;

        if (received === 0) {
          this.canGoNext = false;
          this.canGoPrevious = this.offset > 0;
        } else {
          this.canGoNext = received >= this.recordsPerPage;
          this.canGoPrevious = this.offset > 0;
        }

        this.filteredPartnersData = [...this.partnersData];
        this.loading = false;
      },
      error => {
        this.toastMessage.openSnackBarError('Error loading partner data');
        this.loading = false;
      }
    );
  }

  goToNextPage() {
    if (!this.canGoNext || this.loading) return;
    this.offset += 1;
    this.loadPartnersWithCurrentFilters();
  }

  goToPreviousPage() {
    if (!this.canGoPrevious || this.loading || this.offset <= 0) return;
    this.offset -= 1;
    this.loadPartnersWithCurrentFilters();
  }

  onStatusChange(value: string) {
    this.statusFilter = value || '';
    this.offset = 0;
    this.canGoPrevious = false;
    this.loadPartnersWithCurrentFilters();
  }

  clearFilters() {
    this.mobileFilter = '';
    this.statusFilter = '';
    this.mobileFormControl.setValue('');
    this.offset = 0;
    this.canGoPrevious = false;
    this.loadPartners();
  }

  onKeyPress(event: KeyboardEvent) {
    // Only allow digits 0-9
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  getStatusDisplayText(status: string): string {
    const normalized = status && typeof status === 'string' ? status.toLowerCase() : '';
    switch (normalized) {
      case 'approved': return 'Approved';
      case 'processing': return 'Awaited Approval';
      case 'revoked': return 'Revoked';
      case 'rejected': return 'Rejected';
      default: return status || 'Unknown';
    }
  }

  getAgentName(partner: any): string {
    if (!partner) { return '-'; }
    const firstName = (partner.firstName || '').trim();
    const lastName = (partner.lastName || '').trim();
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || partner.name || partner.agentName || '-';
  }

  formatAadhaar(aadhaar: string | number | undefined | null): string {
    if (!aadhaar) {
      return '-';
    }
    const digitsOnly = String(aadhaar).replace(/\D/g, '');
    if (!digitsOnly) { return '-'; }
    return digitsOnly.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  }

  shouldShowApproveReject(status: string): boolean {
    const normalized = status && typeof status === 'string' ? status.toLowerCase() : '';
    return normalized === 'pending' || normalized === 'processing' || normalized === 'revoked' || normalized === 'rejected';
  }

  shouldShowRevoke(partner: any): boolean {
    // Check the status field for approved state
    const status = partner && partner.status;
    const normalized = status && typeof status === 'string' ? status.toLowerCase() : '';
    return normalized === 'approved';
  }

  shouldShowNoAction(status: string): boolean {
    const normalized = status && typeof status === 'string' ? status.toLowerCase() : '';
    return false; // No status should show "No action required" anymore
  }

  isDisabled(element: any): boolean {
    return !element.mobile || !element.aadhaarNumber || (!element.firstName && !element.lastName);
  }

  approvePartner(partner: any) {
    if (!partner || !partner.userId) { return; }
    const payload = { userId: partner.userId, status: 'approved' };
    this.loading = true;
    this.partnerService.updatePartner(payload).subscribe(
      _ => {
        partner.status = 'approved';
        this.loading = false;
      },
      _ => {
        this.toastMessage.openSnackBarError('Failed to approve');
        this.loading = false;
      }
    );
  }

  rejectPartner(partner: any) {
    if (!partner || !partner.userId) { return; }
    const payload = { userId: partner.userId, status: 'rejected' };
    this.loading = true;
    this.partnerService.updatePartner(payload).subscribe(
      _ => {
        partner.status = 'rejected';
        this.loading = false;
      },
      _ => {
        this.toastMessage.openSnackBarError('Failed to reject');
        this.loading = false;
      }
    );
  }

  revokePartner(partner: any) {
    if (!partner || !partner.userId) { return; }
    const payload = { userId: partner.userId, status: 'revoked' };
    this.loading = true;
    this.partnerService.updatePartner(payload).subscribe(
      _ => {
        partner.status = 'revoked';
        this.loading = false;
      },
      _ => {
        this.toastMessage.openSnackBarError('Failed to revoke');
        this.loading = false;
      }
    );
  }
}
